import { useCallback, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';

import { GET_APP_LEVEL_MODALS_DATA } from '../../../../graphql/app-level-modals/queries/get-modals-data';
import {
  IAppLevelModalsData,
  INotification,
  INotificationNc,
} from '../../../../graphql/app-level-modals/models/get-modals-data.model';
import { usePurchasesConsumerBanModal } from './modals/purchases-consumer-ban/purchases-consumer-ban';
import { useBirthdayPointsModal } from './modals/birthday-points';
import { withAuthorization } from './hocs/with-authorization';
import { useOrderCancelledModal } from './modals/order-cancellation/order-cancellation';
import {
  getBirthdayPointsNotifications,
  getControlVisitNotifications,
  getControlVisitPointsNotifications,
} from './utils';
import { useControlVisitModal } from './modals/control-visit';
import { useControlVisitPointsModal } from './modals/control-visit-points';

const AppLevelModalsComponent = (): null => {
  const { data, updateQuery } = useQuery<IAppLevelModalsData>(GET_APP_LEVEL_MODALS_DATA, {
    pollInterval: 60000,
  });
  const { handleBirthdayPointsModal } = useBirthdayPointsModal();
  const { handlePurchasesConsumerBanModal } = usePurchasesConsumerBanModal();
  const { handleOrderCancelModal } = useOrderCancelledModal();
  const { handleControlVisitModal } = useControlVisitModal();
  const { handleControlVisitPointsModal } = useControlVisitPointsModal();
  const isBirthdayPoints = useMemo((): boolean => {
    return !!getBirthdayPointsNotifications(data?.notificationByAuthId || []).length;
  }, [data?.notificationByAuthId]);
  const cvAvailabilityNotifications = useMemo((): INotification[] => {
    return getControlVisitNotifications(data?.notificationByAuthId);
  }, [data?.notificationByAuthId]);
  const cvPointsNotifications = useMemo((): INotificationNc[] => {
    return getControlVisitPointsNotifications(data?.notificationsNCByAuthId);
  }, [data?.notificationsNCByAuthId]);

  const updateNotifications = useCallback((notificationId: string | string[]): void => {
    updateQuery((prevState) => ({
      ...prevState,
      notificationByAuthId: prevState.notificationByAuthId
        .filter(({ id }) => !(notificationId === id)),
    }));
  }, [
    updateQuery,
  ]);

  useEffect(() => {
    if (data) {
      const {
        notificationByAuthId,
        pointsTransactionByAuthIdAndType,
        notificationsNCByAuthId,
      } = data;

      if (isBirthdayPoints) {
        handleBirthdayPointsModal(notificationByAuthId, pointsTransactionByAuthIdAndType, updateNotifications);
      }

      // modals should appear in certain order
      handlePurchasesConsumerBanModal(notificationByAuthId, updateNotifications);
      handleOrderCancelModal(notificationsNCByAuthId);

      if (cvPointsNotifications.length) {
        handleControlVisitPointsModal(notificationsNCByAuthId);
      }

      if (cvAvailabilityNotifications?.length) {
        handleControlVisitModal(cvAvailabilityNotifications, updateNotifications);
      }
    }
  }, [data, handleControlVisitModal,  handleBirthdayPointsModal, handlePurchasesConsumerBanModal, handleOrderCancelModal, handleControlVisitPointsModal]);

  return null;
};

export const AppLevelModals = withAuthorization(AppLevelModalsComponent);
